<template>
  <div class="cohort-report">
    <div class="toolbar">
      <div class="wrapper">
        <div class="title">
          {{ $t('reports.cohort.title') }}
        </div>
        <div class="t:flex t:justify-between">
          <div class="section">
            <div class="t:space-x-16 t:flex">
              <UiDatePicker
                v-model="dateRange"
                :label="$t('reports.cohort.select_period')"
                :disabled="isDataLoading"
                :preset-dates-factory="getPickerReportOptions"
                :default-value="1"
                :shortcut-name="datePeriod"
                custom-input
                @update:model-value="handlerDatePicker"
              />
              <UiSelectElement
                v-model="cohortType"
                :label="$t('reports.cohort.type')"
                :disabled="isDataLoading"
                :list="cohortTypeOptions"
                width="180"
              />
              <UiSelectElement
                v-model="cohortGroupBy"
                :disabled="isDataLoading"
                :label="$t('reports.cohort.group_by')"
                :list="cohortGroupByOptions"
                width="180"
              />
              <UiSelectElement
                v-model="additionalMetrics"
                :label="$t('reports.cohort.additional_metrics')"
                :list="metricOptions"
                :disabled="isDataLoading"
                option-value-param="name"
                option-name-param="title"
                option-label-param="title"
                multiple
                collapse-tag-tooltip
                :max-collapse-tags="1"
                width="180"
              />
            </div>
            <CohortFilter
              ref="filter"
              v-model="filters"
              :disabled="isDataLoading"
              class="ui-filter-new t:ml-12"
              @submit="applyFilters"
            />
          </div>
          <div class="section">
            <UiCurrencyPicker
              class="t:mx-8"
              :model-value="tmpCurrencyCode"
              :disabled="isDataLoading"
              :currencies="customerCurrencies"
              @update:model-value="changeCurrency"
            />
            <ExportData
              :disabled="$_.isEmpty(exportUrls)
                || isDataLoading"
              :data="configExport"
              @export-data="exportData"
            />
            <UiButton
              icon="sync-alt"
              :disabled="isDataLoading"
              filled
              class="t:ml-12"
              @click="getReport"
            >
              {{ $t('reports.other.generate') }}
            </UiButton>
          </div>
        </div>
      </div>
    </div>
    <div class="ui-table old-table">
      <CohortTable
        ref="cohortTable"
        :loading="isDataLoading"
        :table-data="tableData"
        :metrics-list="metricsList"
        :applied-additional-metrics="appliedAdditionalMetrics"
        :render-cohort-label="renderCohortLabel"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { format, isBefore, startOfISOWeek, endOfISOWeek, isAfter, parse } from 'date-fns';
import updateUrl from '@/service/updateUrl';
import formatCurrency from '@/views/mixins/format-currency';
import datePickerShortcuts from '@/views/mixins/date-picker-shortcuts';
import ExportData from '@/components/ExportData.vue';
import { locales } from '@/helpers/date-locales';
import CohortTable from './components/CohortTable.vue';
import CohortFilter from './components/CohortFilter.vue';

const LS_KEY = 'reports/cohort';

export default {
  name: 'CohortReport',
  components: {
    CohortTable,
    ExportData,
    CohortFilter,
  },
  mixins: [formatCurrency, datePickerShortcuts],
  data() {
    return {
      tableData: {
        cohort_names: [],
        data: {},
      },
      tmpCurrencyCode: '',
      currencyCode: '',
      isDataLoading: false,
      dateRange: [],
      dateRangeGenerated: [],
      datePeriod: '',
      filters: {},

      cohortType: 'registration',
      cohortGroupBy: 'daily',
      cohortGroupByApplied: 'daily',
      additionalMetrics: [],
      appliedAdditionalMetrics: [],

      exportUrls: {},
      configExport: [
        {
          command: 'csv',
          label: 'CSV',
        },
        {
          command: 'xlsx',
          label: 'XLSX',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      customerCurrencies: 'misc/customerCurrencies',
      reportsSettings: 'reports/reportsSettings',
    }),
    metricsList() {
      return [
        { key: 'players', label: this.$t('reports.filters.players') },
        ...this.appliedAdditionalMetrics.map((key) => {
          const metric = this.reportsSettings.cohort_report_metrics?.find((el) => el.name === key);

          if (!metric) return null;

          const { name, title } = this.formatMetric(metric);

          return {
            key: name,
            label: title,
          };
        }).filter((el) => !!el),
      ];
    },
    cohortTypeOptions() {
      return [
        {
          id: 'registration',
          name: this.$t('reports.cohort.types.registration'),
        },
        {
          id: 'first_deposit',
          name: this.$t('reports.cohort.types.first_deposit'),
        },
        {
          id: 'earliest_deposit',
          name: this.$t('reports.cohort.types.earliest_deposit'),
        },
      ];
    },
    cohortGroupByOptions() {
      return [
        {
          id: 'daily',
          name: this.$t('reports.cohort.group.day'),
        },
        {
          id: 'weekly',
          name: this.$t('reports.cohort.group.week'),
        },
        {
          id: 'monthly',
          name: this.$t('reports.cohort.group.month'),
        },
      ];
    },
    metricOptions() {
      return this.reportsSettings.cohort_report_metrics.map((metric) => this.formatMetric(metric));
    },
  },
  watch: {
    filters: {
      handler(nv) {
        this.$ls.set(`${LS_KEY}/filters`, JSON.stringify(nv));
      },
      deep: true,
    },
    currencyCode(nv) {
      this.$ls.set(`${LS_KEY}/currency_code`, nv);
    },
    cohortType(nv) {
      this.$ls.set(`${LS_KEY}/cohort_type`, nv);
    },
    cohortGroupBy(nv) {
      this.$ls.set(`${LS_KEY}/cohort_group_by`, nv);
    },
    appliedAdditionalMetrics(nv) {
      this.$ls.set(`${LS_KEY}/applied_additional_metrics`, nv);
    },
  },
  created() {
    const _filterParams = updateUrl.getParseParams(this.$route.hash, [
      'date',
      'datePeriod',
      'filters',
      'currency_code',
      'cohort_type',
      'cohort_group_by',
      'applied_additional_metrics',
    ]);

    if (!this.$_.isEmpty(_filterParams)) {
      this.filters = this.$_.get(_filterParams, ['filters'], {});
      this.currencyCode = this.checkCurrencyCode(this.$_.get(_filterParams, 'currency_code'));
      this.tmpCurrencyCode = this.checkCurrencyCode(this.$_.get(_filterParams, 'currency_code'));
      this.cohortType = this.$_.get(_filterParams, 'cohort_type', 'registration');
      this.cohortGroupBy = this.$_.get(_filterParams, 'cohort_group_by', 'daily');
      this.cohortGroupByApplied = this.$_.get(_filterParams, 'cohort_group_by', 'daily');

      const metricKeys = this.reportsSettings.cohort_report_metrics.map((el) => el.name);
      const availableMetrics = this.$_.intersection(metricKeys, this.$_.get(_filterParams, 'applied_additional_metrics', []));

      this.appliedAdditionalMetrics = availableMetrics;
      this.additionalMetrics = availableMetrics;

      this.initDate({
        period: _filterParams.datePeriod,
        from: this.$_.get(_filterParams, ['date', 'from']),
        to: this.$_.get(_filterParams, ['date', 'to']),
      });
    } else {
      this.initDate({
        period: this.$route.query.datePeriod,
        from: this.$route.query.from,
        to: this.$route.query.to,
      });
      this.filters = JSON.parse(this.$ls.get(`${LS_KEY}/filters`)) || {};
      this.currencyCode = this.checkCurrencyCode(this.$ls.get(`${LS_KEY}/currency_code`));
      this.tmpCurrencyCode = this.checkCurrencyCode(this.$ls.get(`${LS_KEY}/currency_code`));
      this.cohortType = this.$ls.get(`${LS_KEY}/cohort_type`) || 'registration';
      this.cohortGroupBy = this.$ls.get(`${LS_KEY}/cohort_group_by`) || 'daily';
      this.cohortGroupByApplied = this.$ls.get(`${LS_KEY}/cohort_group_by`) || 'daily';

      const metricKeys = this.reportsSettings.cohort_report_metrics.map((el) => el.name);
      const availableMetrics = this.$_.intersection(metricKeys, this.$ls.get(`${LS_KEY}/applied_additional_metrics`) || []);

      this.appliedAdditionalMetrics = availableMetrics;
      this.additionalMetrics = availableMetrics;
    }
  },
  methods: {
    initDate({ period, from, to }) {
      const pickerOptions = this.getPickerReportOptions();
      const curPeriod = period && pickerOptions.find((el) => el.value.short_title === period);

      if (curPeriod) {
        this.dateRange = curPeriod.value.range;
        this.datePeriod = period;
      } else if (from && to) {
        this.dateRange[0] = from;
        this.dateRange[1] = to;
        this.datePeriod = '';
      } else {
        const lsPeriod = this.$ls.get(`${LS_KEY}/dateperiod`);
        const lsRange = JSON.parse(this.$ls.get(`${LS_KEY}/daterange`));

        if (lsPeriod) {
          const { range, short_title } = lsPeriod
            ? pickerOptions.find((el) => el.value.short_title === lsPeriod).value
            : pickerOptions[1].value;
          this.dateRange = range;
          this.datePeriod = short_title;
        } else if (lsRange) {
          this.dateRange[0] = lsRange[0];
          this.dateRange[1] = lsRange[1];
          this.datePeriod = '';
        } else {
          const shortcut = pickerOptions[1].value;
          this.dateRange = shortcut.range;
          this.datePeriod = shortcut.short_title;
        }
      }
    },
    handlerDatePicker(range, period) {
      if (range) {
        this.isDataLoading = true;
        this.dateRange = this.$_.cloneDeep(range);
        this.datePeriod = period;
        this.isDataLoading = false;
      }
    },
    resetData() {
      this.$refs?.cohortTable?.resetData();
    },
    getStartOfWeekLimited(day, limitDay) {
      const startOfWeek = startOfISOWeek(day);

      return format(isBefore(startOfWeek, limitDay) ? limitDay : startOfWeek, 'dd MMM yyyy', { locale: locales[this.$i18n.locale] });
    },
    getEndOfWeekLimited(day, limitDay) {
      const endOfWeek = endOfISOWeek(day);

      return format(isAfter(endOfWeek, limitDay) ? limitDay : endOfWeek, 'dd MMM yyyy', { locale: locales[this.$i18n.locale] });
    },
    renderCohortLabel(day) {
      const parsedDay = parse(day, 'yyyy-MM-dd', new Date());

      switch (this.cohortGroupByApplied) {
      case 'daily':
        return format(parsedDay, 'dd MMM yyyy', { locale: locales[this.$i18n.locale] });
      case 'weekly':
        return `<span>${this.getStartOfWeekLimited(parsedDay, this.dateRangeGenerated?.[0])}&nbsp;-</span>
                <span>${this.getEndOfWeekLimited(parsedDay, this.dateRangeGenerated?.[1])}</span>`;
      case 'monthly':
        // eslint-disable-next-line no-case-declarations
        const month = format(parsedDay, 'LLLL yyyy', { locale: locales[this.$i18n.locale] });
        return month.charAt(0).toUpperCase() + month.slice(1);
      default:
        return format(parsedDay, 'dd MMM yyyy', { locale: locales[this.$i18n.locale] });
      }
    },
    exportData(format) {
      window.open(this.exportUrls[format]);
    },
    changeCurrency(value) {
      this.tmpCurrencyCode = value;
    },
    applyFilters(val) {
      this.filters = val;
    },
    _updateCompletedUrl() {
      const _data = {
        filters: this.filters,
        cohort_type: this.cohortType,
        cohort_group_by: this.cohortGroupBy,
        currency_code: this.tmpCurrencyCode,
        applied_additional_metrics: this.additionalMetrics,
        datePeriod: this.datePeriod,
        date: {
          from: this.dateRange?.[0] ? `${format(this.dateRange[0], 'yyyy-MM-dd')} 00:00:00` : '',
          to: this.dateRange?.[1] ? `${format(this.dateRange[1], 'yyyy-MM-dd')} 23:59:59` : '',
        },
      };

      this.$ls.set(`${LS_KEY}/daterange`, JSON.stringify(this.dateRange));
      this.$ls.set(`${LS_KEY}/dateperiod`, this.datePeriod);
      updateUrl.updateGetParams(_data);
    },
    dataQuery() {
      const {
        players_filter,
        traffic_type,
        ...restFilters
      } = this.$_.omit(this.filters, ['affiliate', 'marketingMaterial', 'trafficSource', 'affiliateOffer', 'mediaBuyingCampaignRoot', 'mediaBuyingCampaign']);

      return {
        cohort_event_type: this.cohortType,
        cohort_granularity: this.cohortGroupBy,
        currency_code: this.tmpCurrencyCode,
        metrics: this.additionalMetrics.length ? this.additionalMetrics : undefined,
        metrics_format: 'pretty',
        players_filter,
        traffic_type,
        from: this.dateRange?.[0] ? `${format(this.dateRange[0], 'yyyy-MM-dd')} 00:00:00` : '',
        to: this.dateRange?.[1] ? `${format(this.dateRange[1], 'yyyy-MM-dd')} 23:59:59` : '',
        filters: Object.entries(restFilters).reduce((acc, [key, value]) => {
          if (value) {
            acc[key] = {
              op: '=',
              value,
            };
          }

          return acc;
        }, {}),
      };
    },
    async getReport() {
      this.isDataLoading = true;
      this._updateCompletedUrl();
      this.tableData = {
        cohort_names: [],
        data: {},
      };
      this.dateRangeGenerated = this.$_.cloneDeep(this.dateRange);

      try {
        const response = await this.$api.getCohortReport(this.dataQuery());
        const { csv, xlsx } = response.data.misc;
        this.tableData = this.$_.cloneDeep(response.data.payload);
        this.exportUrls = { csv, xlsx };
        this.appliedAdditionalMetrics = this.$_.cloneDeep(this.additionalMetrics);
        this.currencyCode = this.tmpCurrencyCode;
        this.cohortGroupByApplied = this.cohortGroupBy;
      } finally {
        this.resetData();

        const tableBodyWrapper = this.$refs.cohortTable?.$refs.table?.$el?.querySelector('.el-table__body-wrapper .el-scrollbar .el-scrollbar__wrap');
        if (tableBodyWrapper) {
          tableBodyWrapper.scrollLeft = 0;
          tableBodyWrapper.scrollTop = 0;
        }

        this.isDataLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cohort-report {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .old-table.ui-table {
    flex-shrink: 1;
    flex-grow: 1;
    min-height: 0;
    padding-bottom: 36px;
  }

  .toolbar {
    padding: 16px 0;
    border-bottom: 1px solid #eaeaea;
    .wrapper {
      width: 90%;
      min-width: 1140px;
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      flex-wrap: wrap;

      > div {
        width: 100%;
      }

      .title {
        font-size: 24px;
        font-weight: 500;
        margin-bottom: 16px;
      }

      .section {
        display: flex;
        align-items: flex-end;
      }

      .select-label {
        .text {
          margin-bottom: 4px;
          font-size: 14px;
          color: #212529;
        }

        .select {
          width: 180px;
          height: 34px;
        }
      }

      .select-label + .select-label {
        margin-left: 16px;
      }
    }
  }
}
</style>
